import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
  Collapse,
  Badge,
  useTheme,
  IconButton,
  useMediaQuery,
  Drawer as MuiDrawer,
} from "@mui/material";
import { 
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Home as HomeIcon,
  MoneyOff as MoneyOffIcon,
  AttachMoney as AttachMoneyIcon,
  Category as CategoryIcon,
  Assessment as AssessmentIcon,
  Info as InfoIcon,
  AccountBalanceWallet,
  Feedback as FeedbackIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { Drawer } from "../../styles/DrawerStyle";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ABOUT_ROUTE_PATH,
  CATEGORIES_ROUTE_PATH,
  EXPENSES_ROUTE_PATH,
  HOME_ROUTE_PATH,
  INCOMES_ROUTE_PATH,
  MONTHLY_REPORTS_ROUTE_PATH,
  CONTACT_ROUTE_PATH,
} from "../../utils/constants/AppConstants";
import { useAuth } from "../../auth/AuthContext";

interface SidebarMenuComponentProps {
  mobileMenuOpen?: boolean;
  onMobileMenuClose?: () => void;
}

const SidebarMenuComponent = ({ mobileMenuOpen = false, onMobileMenuClose }: SidebarMenuComponentProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const isActive = (path: string) => location.pathname === path;

  const navigationItems = isAuthenticated
    ? [
        {
          section: "Overview",
          items: [
            {
              text: "Dashboard",
              icon: <HomeIcon />,
              path: HOME_ROUTE_PATH,
              badge: null,
            },
          ],
        },
        {
          section: "Financial Management",
          items: [
            {
              text: "Expenses",
              icon: <MoneyOffIcon />,
              path: EXPENSES_ROUTE_PATH,
              badge: null,
            },
            {
              text: "Income",
              icon: <AttachMoneyIcon />,
              path: INCOMES_ROUTE_PATH,
              badge: null,
            },
            {
              text: "Categories",
              icon: <CategoryIcon />,
              path: CATEGORIES_ROUTE_PATH,
              badge: null,
            },
          ],
        },
        {
          section: "Analytics",
          items: [
            {
              text: "Reports",
              icon: <AssessmentIcon />,
              path: MONTHLY_REPORTS_ROUTE_PATH,
              badge: null,
            },
          ],
        },
        {
          section: "Support",
          items: [
            {
              text: "About",
              icon: <InfoIcon />,
              path: ABOUT_ROUTE_PATH,
              badge: null,
            },
            {
              text: "Contact",
              icon: <FeedbackIcon />,
              path: CONTACT_ROUTE_PATH,
              badge: null,
            },
          ],
        },
      ]
    : [
        {
          section: "Welcome",
          items: [
            {
              text: "Home",
              icon: <HomeIcon />,
              path: HOME_ROUTE_PATH,
              badge: null,
            },
            {
              text: "About",
              icon: <InfoIcon />,
              path: ABOUT_ROUTE_PATH,
              badge: null,
            },
          ],
        },
      ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile && onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  const drawerContent = (
    <>
      {/* Header with toggle button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: (isMobile || open) ? "space-between" : "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}
      >
        {(isMobile || open) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceWallet sx={{ color: theme.palette.primary.main, fontSize: "1.5rem" }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Menu
            </Typography>
          </Box>
        )}
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor: theme.palette.primary.light + "20",
              color: theme.palette.primary.main,
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          {(open || mobileMenuOpen) ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      {/* Navigation sections */}
      <Box sx={{ flex: 1, overflowY: "auto", pt: 1 }}>
        {navigationItems.map((section, sectionIndex) => (
          <Box key={section.section} sx={{ mb: 2 }}>
            {/* Section header */}
            <Collapse in={isMobile || open}>
              <Typography
                variant="caption"
                sx={{
                  px: 3,
                  py: 1,
                  display: "block",
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontSize: "0.75rem",
                }}
              >
                {section.section}
              </Typography>
            </Collapse>

            {/* Section items */}
            <List sx={{ py: 0 }}>
              {section.items.map((item, itemIndex) => (
                <ListItem key={item.text} disablePadding sx={{ px: 1 }}>
                  <Tooltip
                    title={(open || isMobile) ? "" : item.text}
                    placement="right"
                    arrow
                  >
                    <ListItemButton
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        minHeight: 48,
                        borderRadius: 2,
                        mx: 1,
                        mb: 0.5,
                        justifyContent: (open || isMobile) ? 'initial' : 'center',
                        px: (open || isMobile) ? 2 : 1,
                        backgroundColor: isActive(item.path)
                          ? theme.palette.primary.light + "20"
                          : "transparent",
                        color: isActive(item.path)
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                        "&:hover": {
                          backgroundColor: isActive(item.path)
                            ? theme.palette.primary.light + "30"
                            : theme.palette.action.hover,
                          transform: "translateX(4px)",
                        },
                        transition: "all 0.2s ease-in-out",
                        "& .MuiListItemIcon-root": {
                          color: isActive(item.path)
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: (open || isMobile) ? 2 : "auto",
                          justifyContent: "center",
                        }}
                      >
                        {item.badge ? (
                          <Badge
                            badgeContent={item.badge}
                            color="secondary"
                            sx={{
                              "& .MuiBadge-badge": {
                                fontSize: "0.6rem",
                                height: "16px",
                                minWidth: "16px",
                              },
                            }}
                          >
                            {item.icon}
                          </Badge>
                        ) : (
                          item.icon
                        )}
                      </ListItemIcon>
                      {(open || isMobile) && (
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: "0.9rem",
                            fontWeight: isActive(item.path) ? 600 : 500,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>

            {/* Divider between sections (except last) */}
            {sectionIndex < navigationItems.length - 1 && (
              <Collapse in={open || isMobile}>
                <Divider sx={{ mx: 2, my: 1 }} />
              </Collapse>
            )}
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Collapse in={open || isMobile}>
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center" }}
          >
            PocketBalance v1.0
          </Typography>
        </Box>
      </Collapse>
    </>
  );

  return (
    <>
      {/* Mobile Drawer - Temporary */}
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={mobileMenuOpen}
          onClose={onMobileMenuClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            "& .MuiDrawer-paper": {
              marginTop: "56px",
              height: "calc(100vh - 56px)",
              width: 280,
              background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
              borderRight: `1px solid ${theme.palette.divider}`,
              overflowX: "hidden",
            },
          }}
        >
          {drawerContent}
        </MuiDrawer>
      ) : (
        /* Desktop Drawer - Permanent */
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            display: { xs: 'none', md: 'block' },
            "& .MuiDrawer-paper": {
              marginTop: "72px",
              height: "calc(100vh - 72px)",
              background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
              borderRight: `1px solid ${theme.palette.divider}`,
              overflowX: "hidden",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
      
    </>
  );
};

export default SidebarMenuComponent;
